import { CONTRACT_CONNECTION_STATUS } from '@/lib/sub-tenant/constants';
import {
  GENERAL_ERRAND_INVOICING_STATUS,
  GENERAL_ERRAND_PRIORITY,
  GENERAL_ERRAND_STATUS,
} from '@/app/errand-configurations/general-errands/handle/[id]/constants';
import {
  INSPECTION_ERRAND_STATUS,
  INSPECTION_ERRAND_VALIDITY_STATUS,
} from '@/app/errand-configurations/inspections/constants';
import {
  GeneralErrandConfig,
  InspectionErrandConfig,
} from '@pigello/pigello-matrix';
import type { BadgeVariant } from '../ui/badge';
import { ROUNDING_OCCURRENCE_STATUS } from '@/app/errand-configurations/rounds/constants';

export type StatusType =
  | 'tenant'
  | 'contract'
  | 'object'
  | 'rounding-status'
  | 'errand-status'
  | 'errand-priority'
  | 'active-status'
  | 'errand-invoicing'
  | 'inspection-status'
  | 'inspection-faults'
  | 'sub-tenant-contract-connection'
  | 'future-contracting'
  | 'brf-membership';

type Status = {
  text: string;
  color: BadgeVariant['variant'];
};

export const StatusMap: Record<StatusType, Record<number, Status>> = {
  'active-status': {
    0: { text: 'Inaktiv', color: 'destructive' },
    1: { text: 'Aktiv', color: 'green' },
  },
  tenant: {
    0: { text: 'Aktiv', color: 'green' },
    1: { text: 'Aktiv', color: 'green' },
    2: { text: 'Aktiv', color: 'green' },
    3: { text: 'Aktiv', color: 'green' },
    4: { text: 'Aktiv', color: 'green' },
    5: { text: 'Aktiv', color: 'green' },
    6: { text: 'Avflyttande', color: 'destructive' },
    7: { text: 'Aktiv', color: 'green' },
    8: { text: 'Aktiv', color: 'green' },
    9: { text: 'Kommande', color: 'blue' },
    10: { text: 'Kommande (ej signerad)', color: 'blue' },
    11: { text: 'Tidigare (Ej signerat)', color: 'yellow' },
    12: { text: 'Inaktiv', color: 'outline' },
  },
  contract: {
    0: { text: 'Aktivt (Tidsbestämt med förlängning)', color: 'green' },
    1: { text: 'Aktivt (Tillsvidare)', color: 'green' },
    2: { text: 'Aktivt (Tidsbestämt)', color: 'green' },
    3: {
      text: 'Uppsagt för omförhandling (Ej bekräftad)',
      color: 'destructive',
    },
    4: { text: 'Uppsagt för avflytt (Ej bekräftad)', color: 'destructive' },
    5: { text: 'Uppsagt för omförhandling', color: 'destructive' },
    6: { text: 'Uppsagt för avflytt', color: 'destructive' },
    7: { text: 'Varning, utgående', color: 'yellow' },
    8: { text: 'Varning, utgående', color: 'yellow' },
    9: { text: 'Kommande', color: 'blue' },
    10: { text: 'Ej signerat', color: 'purple' },
    11: { text: 'Uppsägning ej signerad', color: 'light-gray' },
    12: { text: 'Tidigare', color: 'gray' },
  },
  object: {
    0: { text: 'Uthyrd (Tidsbestämt med förlängning)', color: 'green' },
    1: { text: 'Uthyrd (Tillsvidare)', color: 'green' },
    2: { text: 'Uthyrd (Tidsbestämt)', color: 'green' },
    3: {
      text: 'Uthyrd (Uppsagt för omförhandling, ej bekräftad)',
      color: 'destructive',
    },
    4: {
      text: 'Uthyrd (Uppsagt för avflytt, ej bekräftad)',
      color: 'destructive',
    },
    5: { text: 'Uthyrd (Uppsagt för omförhandling)', color: 'destructive' },
    6: { text: 'Uthyrd (Uppsagt för avflytt)', color: 'destructive' },
    7: { text: 'Uthyrd (Varning, utgående)', color: 'yellow' },
    8: { text: 'Uthyrd (Varning, utgående)', color: 'yellow' },
    9: { text: 'Vakant (Kommande avtal)', color: 'blue' },
    10: { text: 'Vakant (Kommande avtal ej signerat)', color: 'purple' },
    11: { text: 'Vakant (Uppsägning ej signerad)', color: 'destructive' },
    12: { text: 'Vakant', color: 'destructive' },
  },
  'future-contracting': {
    0: { text: 'Saknar kommande avtal', color: 'destructive' },
    1: { text: 'Utkast till kommande avtal', color: 'orange' },
    2: { text: 'Kommande avtal (Ej signerat)', color: 'purple' },
    3: { text: 'Kommande avtal (Signerat)', color: 'blue' },
  },
  'sub-tenant-contract-connection': {
    [CONTRACT_CONNECTION_STATUS.INACTIVE]: {
      text: 'Inaktiv',
      color: 'outline',
    },
    [CONTRACT_CONNECTION_STATUS.ACTIVE]: {
      text: 'Aktiv',
      color: 'green',
    },
    [CONTRACT_CONNECTION_STATUS.UPCOMING]: {
      text: 'Kommande',
      color: 'blue',
    },
    [CONTRACT_CONNECTION_STATUS.FORMER]: {
      text: 'Tidigare',
      color: 'gray',
    },
    [CONTRACT_CONNECTION_STATUS.ACTIVE_INDEFINITE]: {
      text: 'Aktiv (Tillsvidare)',
      color: 'green',
    },
  },
  'rounding-status': {
    [ROUNDING_OCCURRENCE_STATUS.CREATED]: {
      text: 'Skapad',
      color: 'yellow',
    },
    [ROUNDING_OCCURRENCE_STATUS.ACCEPTED]: {
      text: 'Accepterad',
      color: 'yellow',
    },
    [ROUNDING_OCCURRENCE_STATUS.STARTED]: {
      text: 'Påbörjad',
      color: 'blue',
    },
    [ROUNDING_OCCURRENCE_STATUS.PAUSED]: {
      text: 'Pausad',
      color: 'yellow',
    },
    [ROUNDING_OCCURRENCE_STATUS.COMPLETED]: {
      text: 'Slutförd',
      color: 'green',
    },
  },
  'errand-status': {
    [GENERAL_ERRAND_STATUS.CREATED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.CREATED
        )?.d ?? 'Okänd status',
      color: 'default',
    },
    [GENERAL_ERRAND_STATUS.ACCEPTED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.ACCEPTED
        )?.d ?? 'Okänd status',
      color: 'green',
    },
    [GENERAL_ERRAND_STATUS.STARTED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.STARTED
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [GENERAL_ERRAND_STATUS.PAUSED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.PAUSED
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [GENERAL_ERRAND_STATUS.COMPLETED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.COMPLETED
        )?.d ?? 'Okänd status',
      color: 'gray',
    },
    [GENERAL_ERRAND_STATUS.COMPLETED_REPORTER]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.COMPLETED_REPORTER
        )?.d ?? 'Okänd status',
      color: 'gray',
    },
    [GENERAL_ERRAND_STATUS.ANNULLED]: {
      text:
        GeneralErrandConfig.fields.status.choices?.at(
          GENERAL_ERRAND_STATUS.ANNULLED
        )?.d ?? 'Okänd status',
      color: 'destructive',
    },
  },
  'errand-priority': {
    [GENERAL_ERRAND_PRIORITY.LOW]: {
      text:
        GeneralErrandConfig.fields.priority.choices?.at(
          GENERAL_ERRAND_PRIORITY.LOW
        )?.d ?? 'Okänd prioritet',
      color: 'yellow',
    },
    [GENERAL_ERRAND_PRIORITY.MEDIUM]: {
      text:
        GeneralErrandConfig.fields.priority.choices?.at(
          GENERAL_ERRAND_PRIORITY.MEDIUM
        )?.d ?? 'Okänd prioritet',
      color: 'orange',
    },
    [GENERAL_ERRAND_PRIORITY.HIGH]: {
      text:
        GeneralErrandConfig.fields.priority.choices?.at(
          GENERAL_ERRAND_PRIORITY.HIGH
        )?.d ?? 'Okänd prioritet',
      color: 'destructive',
    },
    [GENERAL_ERRAND_PRIORITY.HIGHEST]: {
      text:
        GeneralErrandConfig.fields.priority.choices?.at(
          GENERAL_ERRAND_PRIORITY.HIGHEST
        )?.d ?? 'Okänd prioritet',
      color: 'purple',
    },
  },
  'errand-invoicing': {
    [GENERAL_ERRAND_INVOICING_STATUS.MISSING_COSTS]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.MISSING_COSTS
        )?.d ?? 'Okänd status',
      color: 'destructive',
    },
    [GENERAL_ERRAND_INVOICING_STATUS.PENDING_ERRAND_COST_DATA]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.PENDING_ERRAND_COST_DATA
        )?.d ?? 'Okänd status',
      color: 'orange',
    },
    [GENERAL_ERRAND_INVOICING_STATUS.PENDING_INVOICE_CREATION]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.PENDING_INVOICE_CREATION
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [GENERAL_ERRAND_INVOICING_STATUS.PENDING_INVOICE_ATTEST]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.PENDING_INVOICE_ATTEST
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [GENERAL_ERRAND_INVOICING_STATUS.PENDING_PARTLY_INVOICE_ATTEST]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.PENDING_PARTLY_INVOICE_ATTEST
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [GENERAL_ERRAND_INVOICING_STATUS.ALL_INVOICES_ATTESTED]: {
      text:
        GeneralErrandConfig.fields.invoicingStatus.choices?.at(
          GENERAL_ERRAND_INVOICING_STATUS.ALL_INVOICES_ATTESTED
        )?.d ?? 'Okänd status',
      color: 'green',
    },
  },
  'inspection-status': {
    [INSPECTION_ERRAND_STATUS.CREATED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.CREATED
        )?.d ?? 'Okänd status',
      color: 'default',
    },
    [INSPECTION_ERRAND_STATUS.ACCEPTED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.ACCEPTED
        )?.d ?? 'Okänd status',
      color: 'green',
    },
    [INSPECTION_ERRAND_STATUS.STARTED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.STARTED
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [INSPECTION_ERRAND_STATUS.PAUSED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.PAUSED
        )?.d ?? 'Okänd status',
      color: 'yellow',
    },
    [INSPECTION_ERRAND_STATUS.COMPLETED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.COMPLETED
        )?.d ?? 'Okänd status',
      color: 'gray',
    },
    [INSPECTION_ERRAND_STATUS.ANNULLED]: {
      text:
        InspectionErrandConfig.fields.status.choices?.at(
          INSPECTION_ERRAND_STATUS.ANNULLED
        )?.d ?? 'Okänd status',
      color: 'destructive',
    },
  },
  'brf-membership': {
    0: { text: 'Inaktiv', color: 'outline' },
    1: { text: 'Tidigare', color: 'gray' },
    2: { text: 'Kommande', color: 'blue' },
    3: { text: 'Nuvarande', color: 'green' },
  },
  'inspection-faults': {
    [INSPECTION_ERRAND_VALIDITY_STATUS.VALID]: {
      text:
        InspectionErrandConfig.fields.validityStatus.choices?.at(
          INSPECTION_ERRAND_VALIDITY_STATUS.VALID
        )?.d ?? 'Okänd status',
      color: 'green',
    },
    [INSPECTION_ERRAND_VALIDITY_STATUS.INVALID]: {
      text:
        InspectionErrandConfig.fields.validityStatus.choices?.at(
          INSPECTION_ERRAND_VALIDITY_STATUS.INVALID
        )?.d ?? 'Okänd status',
      color: 'destructive',
    },
  },
};

type BadgeProp = {
  text: string;
  variant: BadgeVariant['variant'];
};

export const readableStatusMonitoring: Record<number, BadgeProp> = {
  666: {
    text: 'Okänd',
    variant: 'destructive',
  },
  0: {
    text: 'Skapades',
    variant: 'green',
  },
  1: {
    text: 'Uppdaterades',
    variant: 'yellow',
  },
  2: {
    text: 'Raderades',
    variant: 'destructive',
  },
};
